/**
 * document-list-export.ts — eksport widoku listy do CSV dla list klas dokumentów (E16): umowy,
 * pisma, dokumenty handlowe i wewnętrzne. Cztery strony są zbudowane tak samo, więc to, co wspólne,
 * żyje tutaj, a strona podaje tylko swoje kolumny:
 *  - `buildDocumentListFilterParams` — parametry filtrów listy BEZ stronicowania i projekcji
 *    kolumn (wyszukiwarka, „Pokaż zarchiwizowane”, firma, filtry kolumn, sort + filtry specyficzne
 *    klasy). Tę samą funkcję woła zapytanie listy i eksport — plik ma dokładnie to, co lista.
 *  - `useDocumentListExport` — `useEngineListExport` z ustawieniami dokumentów: trasa
 *    jeden adres `GET /api/list-export/<klasa>`, przy „Eksporcie zaznaczonych” zostaje też przełącznik archiwum (inaczej
 *    zaznaczone dokumenty zarchiwizowane wypadłyby z pliku), a slugi towarzyszy kolumn (np. NIP do
 *    „Strony”/„Nadawcy”, koniec okresu do „Okresu”) idą do projekcji zawsze — bez nich komórka
 *    w pliku byłaby niepełna.
 *  - `counterpartyText` — komórka „nazwa + NIP” (strona umowy, nadawca pisma) jako tekst
 *    „nazwa, NIP <numer>” zamiast sklejenia z ozdobnika.
 */

import type { ColumnCompanionRule } from '@/lib/ext-list-columns';
import {
  useEngineListExport,
  type UseEngineListExportOptions,
  type UseEngineListExportResult,
} from '@/lib/list-export/useEngineListExport';

/** Nazwa parametru przełącznika „Pokaż zarchiwizowane” na listach 4 klas dokumentów. */
export const DOCUMENT_ARCHIVED_PARAM = 'include_deleted';

/** Parametry zachowane przy eksporcie zaznaczonych: firma, sort i przełącznik archiwum. */
export const DOCUMENT_EXPORT_SELECTED_KEEP_PARAMS = [
  'companyId',
  'sort',
  'order',
  DOCUMENT_ARCHIVED_PARAM,
] as const;

export interface DocumentListFilterState {
  /** Fraza wyszukiwarki (po debounce). */
  search: string;
  /** „Pokaż zarchiwizowane”. */
  showArchived: boolean;
  /** Wybrana firma; `null` = tryb „Wszystkie firmy”. */
  companyId: string | null | undefined;
  /** Filtry kolumn zserializowane do JSON (`headerFilters`); `undefined` = brak. */
  headerFilters: string | undefined;
  /** Slug sortu; pusty = domyślny sort serwera. */
  sort?: string | null;
  order: string;
  /** Filtry specyficzne klasy (np. `documentTypeId` dokumentów wewnętrznych); puste pomijane. */
  extra?: Readonly<Record<string, string | null | undefined>>;
}

export function buildDocumentListFilterParams(state: DocumentListFilterState): URLSearchParams {
  const params = new URLSearchParams();
  if (state.search) params.set('q', state.search);
  if (state.showArchived) params.set(DOCUMENT_ARCHIVED_PARAM, 'true');
  if (state.companyId) params.set('companyId', state.companyId);
  for (const [key, value] of Object.entries(state.extra ?? {})) {
    if (value) params.set(key, value);
  }
  if (state.headerFilters) params.set('headerFilters', state.headerFilters);
  // Sort serwerowy (silnik list) — `sort` pominięty gdy brak (domyślny sort backendu), `order` zawsze.
  if (state.sort) params.set('sort', state.sort);
  params.set('order', state.order);
  return params;
}

/** Komórka „nazwa + NIP” jako tekst do pliku: „nazwa, NIP <numer>” (bez NIP-u — sama nazwa). */
export function counterpartyText(row: {
  counterpartyNameCached?: string | null;
  counterpartyTaxIdCached?: string | null;
}): string {
  const name = row.counterpartyNameCached ?? '';
  const taxId = row.counterpartyTaxIdCached ?? '';
  if (!taxId) return name;
  return name ? `${name}, NIP ${taxId}` : `NIP ${taxId}`;
}

export type UseDocumentListExportOptions<TRow> = Omit<
  UseEngineListExportOptions<TRow>,
  'selectedKeepParams' | 'extraSlugs'
> & {
  /** Reguły towarzyszy kolumn strony (te same co dla `?columns=` listy). */
  companions?: readonly ColumnCompanionRule[];
};

export function useDocumentListExport<TRow>(
  options: UseDocumentListExportOptions<TRow>,
): UseEngineListExportResult {
  const { companions = [], ...rest } = options;
  return useEngineListExport<TRow>({
    ...rest,
    selectedKeepParams: DOCUMENT_EXPORT_SELECTED_KEEP_PARAMS,
    extraSlugs: companions.map((c) => c.addSlug),
  });
}
